<template>
  <div class="page boss-page project-detail-page">
    <van-nav-bar :title="project.name || '项目详情'" left-arrow fixed placeholder @click-left="$router.back()"/>

    <van-loading v-if="loading" class="loading" color="#c62828">加载中</van-loading>
    <template v-else>
      <section class="hero">
        <span class="code">{{ value(project, 'project_code') || '—' }}</span>
        <p v-if="value(project, 'description')" class="desc">{{ value(project, 'description') }}</p>
      </section>

      <section class="summary">
        <div><span>总投入</span><b>{{ money(snapshot.totalInvestment) }}</b></div>
        <div><span>总价值</span><b>{{ money(snapshot.totalValue) }}</b></div>
        <div><span>ROI</span><b>{{ snapshot.roiPercent == null ? '--' : snapshot.roiPercent + '%' }}</b></div>
        <div><span>健康度</span><b :class="healthClass(snapshot.healthLevel)">{{ snapshot.healthScore || 0 }}</b></div>
      </section>

      <button class="next-entry" @click="goPlanning">
        <van-icon name="cluster-o"/>
        <span><b>任务拆分与分配</b><small>AI 拆任务 → 人工复审 → 主管分配</small></span>
        <em>进入</em>
      </button>

      <section class="panel">
        <header><h3>成员投入</h3><button type="button" class="add-btn" @click="open('member')">新增</button></header>
        <article v-for="row in detail.members" :key="row.id || row.ID" class="row-item">
          {{ employeeName(value(row, 'employee_id')) }} · {{ value(row, 'role_name') }} · 投入 {{ value(row, 'allocation_percent') }}%
        </article>
        <van-empty v-if="!detail.members.length" description="尚未添加成员" image-size="56"/>
      </section>

      <section class="panel">
        <header><h3>价值台账</h3><button type="button" class="add-btn" @click="open('value')">新增</button></header>
        <article v-for="row in detail.valueEvents" :key="row.id || row.ID" class="row-item">
          {{ value(row, 'event_date') }} · {{ eventLabel(value(row, 'event_type')) }} · {{ money(value(row, 'amount')) }}
        </article>
        <van-empty v-if="!detail.valueEvents.length" description="暂无价值事件" image-size="56"/>
      </section>

      <section class="panel">
        <header><h3>项目成果</h3><button type="button" class="add-btn" @click="open('outcome')">新增</button></header>
        <article v-for="row in detail.outcomes" :key="row.id || row.ID" class="row-item">
          {{ value(row, 'title') }} · {{ statusLabel(value(row, 'status')) }}
        </article>
        <van-empty v-if="!detail.outcomes.length" description="暂无成果" image-size="56"/>
      </section>

      <section class="panel">
        <header><h3>里程碑</h3><button type="button" class="add-btn" @click="open('milestone')">新增</button></header>
        <article v-for="row in detail.milestones" :key="row.id || row.ID" class="row-item">
          {{ value(row, 'name') }} · {{ value(row, 'planned_date') }} · {{ value(row, 'actual_progress') }}%
        </article>
        <van-empty v-if="!detail.milestones.length" description="暂无里程碑" image-size="56"/>
      </section>

      <section class="panel">
        <header><h3>风险与机会</h3><button type="button" class="add-btn" @click="open('risk')">新增</button></header>
        <article v-for="row in detail.risks" :key="row.id || row.ID" class="row-item">
          {{ value(row, 'item_type') === 'RISK' ? '风险' : '机会' }} · {{ value(row, 'title') }} · 概率 {{ value(row, 'probability_percent') }}%
        </article>
        <van-empty v-if="!detail.risks.length" description="暂无风险或机会" image-size="56"/>
      </section>
    </template>

    <van-action-sheet v-model="show" :title="titles[type]">
      <div class="form">
        <template v-if="needsEmployee">
          <van-field readonly clickable label="所属部门" :value="department || '全部部门'" right-icon="arrow" @click="showDepartments=true"/>
          <van-field readonly clickable :label="type==='member'?'项目成员':'负责人'" :value="selectedEmployeeName" placeholder="请选择员工" right-icon="arrow" @click="showEmployees=true"/>
        </template>
        <template v-if="type==='member'">
          <van-field v-model="form.roleName" label="项目角色"/>
          <van-field v-model="form.allocationPercent" type="number" label="投入比例 %"/>
        </template>
        <template v-else-if="type==='value'">
          <van-field v-model="form.eventDate" type="date" label="发生日期"/>
          <van-field readonly clickable :value="eventLabel(form.eventType)" label="类型" @click="showTypes=true"/>
          <van-field v-model="form.amount" type="number" label="金额"/>
          <van-field v-model="form.description" label="说明"/>
        </template>
        <template v-else-if="type==='outcome'">
          <van-field v-model="form.title" required label="成果名称"/>
          <van-field v-model="form.evidenceReference" label="证据链接"/>
          <van-field v-model="form.description" type="textarea" label="成果说明"/>
        </template>
        <template v-else-if="type==='milestone'">
          <van-field v-model="form.name" required label="里程碑"/>
          <van-field v-model="form.plannedDate" type="date" label="计划日期"/>
          <van-field v-model="form.actualProgress" type="number" label="实际进度 %"/>
        </template>
        <template v-else-if="type==='risk'">
          <van-field readonly clickable :value="form.itemType==='OPPORTUNITY'?'机会':'风险'" label="类型" @click="form.itemType=form.itemType==='RISK'?'OPPORTUNITY':'RISK'"/>
          <van-field v-model="form.title" required label="名称"/>
          <van-field v-model="form.probabilityPercent" type="number" label="发生概率 %"/>
          <van-field v-model="form.impactAmount" type="number" label="影响金额"/>
          <van-field v-model="form.responsePlan" type="textarea" label="应对方案"/>
        </template>
      </div>
      <van-button class="save" block type="danger" :loading="saving" @click="save">保存</van-button>
    </van-action-sheet>
    <van-action-sheet v-model="showDepartments" :actions="departmentActions" @select="selectDepartment"/>
    <van-action-sheet v-model="showEmployees" :actions="employeeActions" @select="selectEmployee"/>
    <van-action-sheet v-model="showTypes" :actions="types" @select="selectType"/>
  </div>
</template>
<script>
import api from '@/utils/coreapi';

export default {
  data() {
    return {
      loading: false,
      saving: false,
      show: false,
      showDepartments: false,
      showEmployees: false,
      showTypes: false,
      type: 'member',
      detail: {members: [], valueEvents: [], outcomes: [], milestones: [], risks: []},
      employees: [],
      department: '',
      form: {},
      titles: {
        member: '新增项目成员',
        value: '记录价值事件',
        outcome: '登记项目成果',
        milestone: '新增里程碑',
        risk: '新增风险或机会'
      },
      types: [
        {name: '直接成本', value: 'DIRECT_COST'},
        {name: '其他成本', value: 'OTHER_COST'},
        {name: '收入', value: 'REVENUE'},
        {name: '节约', value: 'SAVING'},
        {name: '风险损失', value: 'RISK_LOSS'},
        {name: '线索', value: 'LEAD'},
        {name: '成交', value: 'DEAL'},
        {name: '品牌贡献', value: 'BRAND_CONTRIBUTION'}
      ]
    };
  },
  computed: {
    project() {
      return this.detail.project || {};
    },
    snapshot() {
      return this.detail.snapshot || {};
    },
    needsEmployee() {
      return ['member', 'outcome', 'milestone', 'risk'].includes(this.type);
    },
    departments() {
      return [...new Set(this.employees.map(x => x.departmentName).filter(Boolean))];
    },
    departmentActions() {
      return [{name: '全部部门', value: ''}, ...this.departments.map(x => ({name: x, value: x}))];
    },
    filteredEmployees() {
      return this.department ? this.employees.filter(x => x.departmentName === this.department) : this.employees;
    },
    employeeActions() {
      return this.filteredEmployees.map(x => ({name: `${x.employeeName} · ${x.positionName || ''}`, value: x.employeeId}));
    },
    selectedEmployeeName() {
      const id = this.form.employeeId || this.form.ownerEmployeeId;
      const x = this.employees.find(v => Number(v.employeeId) === Number(id));
      return x ? `${x.employeeName} · ${x.positionName || ''}` : '';
    }
  },
  mounted() {
    this.load();
  },
  methods: {
    goPlanning() {
      this.$router.push({name: 'BossProjectTaskPlanning', params: {id: this.$route.params.id}});
    },
    healthClass(level) {
      const v = String(level || '').toLowerCase();
      return v === 'healthy' ? 'healthy' : v === 'risk' ? 'risk' : '';
    },
    async load() {
      this.loading = true;
      try {
        const [detail, employees] = await Promise.all([
          api.bossProjectDetail(this.$route.params.id),
          api.bossEmployees()
        ]);
        this.detail = detail;
        this.employees = employees || [];
      } catch (e) {
        this.$toast.fail(e.message || '加载失败');
      } finally {
        this.loading = false;
      }
    },
    open(type) {
      const today = new Date().toISOString().slice(0, 10);
      this.type = type;
      this.department = '';
      this.form = {
        eventType: 'DIRECT_COST',
        eventDate: today,
        itemType: 'RISK',
        startDate: today,
        endDate: today,
        plannedDate: today,
        allocationPercent: 100,
        actualProgress: 0,
        probabilityPercent: 50,
        impactAmount: 0
      };
      this.show = true;
    },
    selectDepartment(a) {
      this.department = a.value;
      this.form.employeeId = null;
      this.form.ownerEmployeeId = null;
      this.showDepartments = false;
    },
    selectEmployee(a) {
      if (this.type === 'member') this.$set(this.form, 'employeeId', a.value);
      else this.$set(this.form, 'ownerEmployeeId', a.value);
      this.showEmployees = false;
    },
    async save() {
      if (this.needsEmployee && !(this.form.employeeId || this.form.ownerEmployeeId)) return this.$toast('请选择员工');
      this.saving = true;
      try {
        const id = this.$route.params.id;
        const f = {...this.form};
        ['employeeId', 'ownerEmployeeId'].forEach(k => {
          if (f[k]) f[k] = Number(f[k]);
        });
        ['allocationPercent', 'amount', 'actualProgress', 'probabilityPercent', 'impactAmount'].forEach(k => {
          if (f[k] != null) f[k] = Number(f[k]);
        });
        if (this.type === 'member') {
          await api.addBossProjectMember(id, {...f, roleName: f.roleName || '项目成员', hourlyCost: 0, plannedHours: 0});
        } else if (this.type === 'value') {
          await api.addBossProjectValue(id, {...f, confirmationStatus: 'CONFIRMED', paymentStatus: 'UNPAID', attributionPercent: 100});
        } else if (this.type === 'outcome') {
          await api.addBossProjectOutcome(id, {...f, outcomeType: 'DELIVERABLE', status: 'PENDING'});
        } else if (this.type === 'milestone') {
          await api.addBossProjectMilestone(id, {...f, status: 'PENDING'});
        } else {
          await api.addBossProjectRisk(id, {...f, impactLevel: 'MEDIUM', status: 'OPEN', sourceType: 'MANUAL'});
        }
        this.show = false;
        await this.load();
        this.$toast.success('保存成功');
      } catch (e) {
        this.$toast.fail(e.message || '保存失败');
      } finally {
        this.saving = false;
      }
    },
    selectType(a) {
      this.form.eventType = a.value;
      this.showTypes = false;
    },
    employeeName(id) {
      const x = this.employees.find(v => Number(v.employeeId) === Number(id));
      return x ? x.employeeName : `员工 ${id}`;
    },
    value(o, k) {
      return o[k] !== undefined ? o[k] : o[k.toUpperCase()];
    },
    money(v) {
      return '¥' + Number(v || 0).toLocaleString();
    },
    eventLabel(v) {
      return {
        DIRECT_COST: '直接成本',
        OTHER_COST: '其他成本',
        REVENUE: '收入',
        SAVING: '节约',
        RISK_LOSS: '风险损失',
        LEAD: '线索',
        DEAL: '成交',
        BRAND_CONTRIBUTION: '品牌贡献'
      }[v] || '请选择';
    },
    statusLabel(v) {
      return {PENDING: '待审核', SUBMITTED: '待审核', VERIFIED: '已通过', REJECTED: '已驳回'}[v] || v;
    }
  }
};
</script>
<style lang="less" scoped>
@import './boss.less';

.project-detail-page {
  min-height: 100vh;
  margin: 0;
  padding-bottom: 1.2rem;
  background: #f6f7fb;
  color: #292d3d;
}

.project-detail-page > .van-nav-bar.van-nav-bar--fixed,
.project-detail-page > .van-nav-bar,
.project-detail-page /deep/ .van-nav-bar {
  top: 0 !important;
  left: 0 !important;
  margin: 0 !important;
  width: 100vw !important;
  max-width: 100% !important;
  border-radius: 0 !important;
}

.loading {
  padding: 2rem;
  text-align: center;
}

.hero {
  margin: .2rem .28rem 0;
  padding: .22rem .26rem;
  background: #fff;
  border-radius: .16rem;
  box-shadow: 0 .05rem .2rem rgba(30, 35, 60, .06);
}

.hero .code {
  color: #8b8e9d;
  font-size: .22rem;
}

.hero .desc {
  margin: .1rem 0 0;
  color: #555;
  font-size: .24rem;
  line-height: 1.55;
}

.summary {
  margin: .2rem .28rem 0;
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  background: #fff;
  border-radius: .16rem;
  box-shadow: 0 .05rem .2rem rgba(30, 35, 60, .06);
}

.summary div {
  padding: .22rem .06rem;
  text-align: center;
  border-right: 1PX solid #f0f0f0;
}

.summary div:last-child {
  border-right: 0;
}

.summary span,
.summary b {
  display: block;
}

.summary span {
  color: #8b8e9d;
  font-size: .21rem;
}

.summary b {
  margin-top: .06rem;
  font-size: .28rem;
  color: #34364a;
}

.summary b.healthy {
  color: #278e56;
}

.summary b.risk {
  color: #c62828;
}

.next-entry {
  width: calc(100% - .56rem);
  margin: .2rem .28rem 0;
  min-height: 1.2rem;
  padding: .2rem .22rem;
  display: flex;
  align-items: center;
  gap: .16rem;
  text-align: left;
  border: 0;
  border-radius: .16rem;
  background: #fff;
  box-shadow: 0 .05rem .2rem rgba(30, 35, 60, .06);
  cursor: pointer;
}

.next-entry:active {
  background: #fafafa;
}

.next-entry > .van-icon:first-child {
  width: .68rem;
  height: .68rem;
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 .68rem;
  color: #c62828;
  font-size: .36rem;
  border-radius: .14rem;
  background: #fff5f5;
}

.next-entry span {
  min-width: 0;
  flex: 1;
}

.next-entry b,
.next-entry small {
  display: block;
}

.next-entry b {
  font-size: .29rem;
  color: #34364a;
}

.next-entry small {
  margin-top: .05rem;
  color: #8b8e9d;
  font-size: .22rem;
}

.next-entry em {
  flex: 0 0 auto;
  min-width: .88rem;
  padding: .1rem .22rem;
  color: #fff;
  text-align: center;
  border-radius: .28rem;
  background: linear-gradient(135deg, #d32f2f, #b71c1c);
  font-size: .23rem;
  font-style: normal;
  font-weight: 500;
}

.panel {
  margin: .2rem .28rem 0;
  padding: .22rem .26rem .12rem;
  background: #fff;
  border-radius: .16rem;
  box-shadow: 0 .05rem .2rem rgba(30, 35, 60, .06);
}

.panel header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: .06rem;
}

.panel h3 {
  margin: 0;
  padding-left: .12rem;
  font-size: .3rem;
  color: #30313a;
  border-left: .06rem solid #c6282d;
}

.add-btn {
  padding: .06rem .18rem;
  color: #c62828;
  font-size: .22rem;
  border: 1PX solid #f0c8c8;
  border-radius: .24rem;
  background: #fff5f5;
  cursor: pointer;
}

.row-item {
  padding: .18rem 0;
  border-top: 1PX solid #f0f0f0;
  font-size: .25rem;
  color: #555;
  line-height: 1.45;
}

.row-item:first-of-type {
  border-top: 0;
}

.form {
  padding: .1rem .22rem;
}

.save {
  margin: .2rem .28rem .32rem;
  width: calc(100% - .56rem);
  height: .88rem;
  border-radius: .12rem;
  font-size: .28rem;
  font-weight: 500;
}
</style>



// WEBPACK FOOTER //
// src/views/boss/ProjectDetail.vue